"use client";

import { useEffect, useRef, useState } from "react";

export interface InfographicCardProps {
  title: string;
  imageUrl: string | null;
  pdfUrl: string | null;
  filterType?: string;
}

export default function InfographicCard({
  title,
  imageUrl,
  pdfUrl,
  filterType,
}: InfographicCardProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const scrollPositionRef = useRef(0);
  const directionRef = useRef<"down" | "up">("down");

  // Intersection observer for entrance animation
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Auto-scroll animation (camera pan effect)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !isVisible || isLightboxOpen) return;

    const img = container.querySelector("img") as HTMLImageElement | null;
    if (!img) return;

    function startAnimation() {
      const maxScroll = img!.offsetHeight - container!.offsetHeight;
      if (maxScroll <= 0) return;

      const speed = isHovered ? 0 : 0.4;

      function animate() {
        if (speed === 0) {
          animationRef.current = requestAnimationFrame(animate);
          return;
        }

        if (directionRef.current === "down") {
          scrollPositionRef.current += speed;
          if (scrollPositionRef.current >= maxScroll) {
            scrollPositionRef.current = maxScroll;
            directionRef.current = "up";
          }
        } else {
          scrollPositionRef.current -= speed;
          if (scrollPositionRef.current <= 0) {
            scrollPositionRef.current = 0;
            directionRef.current = "down";
          }
        }

        container!.scrollTop = scrollPositionRef.current;
        animationRef.current = requestAnimationFrame(animate);
      }

      animationRef.current = requestAnimationFrame(animate);
    }

    // Wait for image load
    if (img.complete) {
      startAnimation();
    } else {
      img.onload = startAnimation;
    }

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isVisible, isHovered, isLightboxOpen]);

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isLightboxOpen]);

  const fallbackImg = "/br1.jpg";

  return (
    <>
      <div
        ref={cardRef}
        className={`infographic-card-modern h-100${isVisible ? " infographic-card-visible" : ""}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Viewport wrapper (position context for fixed overlay) */}
        <div
          className="infographic-card-viewport-wrap"
          onClick={() => imageUrl && setIsLightboxOpen(true)}
        >
          {/* Scroll-animated image viewport */}
          <div ref={scrollContainerRef} className="infographic-card-viewport">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={imageUrl || fallbackImg}
              alt={title}
              className="infographic-card-img"
              draggable={false}
            />
          </div>

          {/* Hover overlay - outside scroll container so it stays fixed */}
          <div
            className={`infographic-card-overlay${isHovered ? " infographic-card-overlay-visible" : ""}`}
          >
            <button className="infographic-card-zoom-btn" type="button">
              <i className="fa fa-search-plus"></i>
              <span>View Full</span>
            </button>
          </div>
        </div>

        {/* Card body */}
        <div className="infographic-card-body">
          <div className="infographic-card-meta">
            {filterType && (
              <span className="infographic-card-tag">
                <i className="fa-solid fa-tag"></i> {filterType}
              </span>
            )}
          </div>
          <h4 className="infographic-card-title">{title}</h4>
          <div className="infographic-card-actions">
            {imageUrl && (
              <a
                href={imageUrl}
                target="_blank"
                rel="noreferrer nofollow"
                download
                className="infographic-card-action-btn"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="fa fa-image"></i> Image
              </a>
            )}
            {pdfUrl && pdfUrl.length > 0 && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer nofollow"
                className="infographic-card-action-btn infographic-card-action-pdf"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="fa fa-file-pdf"></i> PDF
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {isLightboxOpen && imageUrl && (
        <div
          className="infographic-lightbox"
          onClick={() => setIsLightboxOpen(false)}
        >
          <button
            className="infographic-lightbox-close"
            onClick={() => setIsLightboxOpen(false)}
            type="button"
          >
            <i className="far fa-times-circle"></i>
          </button>
          <div
            className="infographic-lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={imageUrl} alt={title} loading="lazy" decoding="async" />
          </div>
        </div>
      )}
    </>
  );
}
